import { DomainError } from "./errors";

/**
 * Money is always stored as an integer number of the currency's smallest
 * invoiced unit (HUF: 1 forint, EUR: 1 cent). Never use floating point
 * amounts for storage or totals.
 */
export interface CurrencyInfo {
  code: string;
  /** Decimals used on invoices and totals. */
  decimals: number;
}

export const CURRENCIES: Record<string, CurrencyInfo> = {
  HUF: { code: "HUF", decimals: 0 },
  EUR: { code: "EUR", decimals: 2 },
  CZK: { code: "CZK", decimals: 2 },
  PLN: { code: "PLN", decimals: 2 },
  RON: { code: "RON", decimals: 2 },
  RSD: { code: "RSD", decimals: 2 },
  UAH: { code: "UAH", decimals: 2 },
};

export function currency(code: string): CurrencyInfo {
  const c = CURRENCIES[code];
  if (!c) throw new DomainError("CURRENCY_MISMATCH", `unknown currency ${code}`);
  return c;
}

/**
 * Commercial rounding: half away from zero. A small epsilon guards against
 * binary floating point artefacts such as 1.005 * 100 = 100.49999.
 */
export function roundHalfAwayFromZero(value: number): number {
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(value) + 1e-9);
}

/** Converts a decimal amount (as typed by a user, e.g. 12.5 EUR) to minor units. */
export function toMinor(amount: number, currencyCode: string): number {
  return roundHalfAwayFromZero(amount * 10 ** currency(currencyCode).decimals);
}

/** Converts minor units back to a decimal amount for display. */
export function fromMinor(minor: number, currencyCode: string): number {
  return minor / 10 ** currency(currencyCode).decimals;
}

/** Locale aware formatting, e.g. "1 234 Ft" (hu) or "1.234,50 €" (de). */
export function formatMoney(minor: number, currencyCode: string, locale: string): string {
  const c = currency(currencyCode);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: c.code,
    minimumFractionDigits: c.decimals,
    maximumFractionDigits: c.decimals,
  }).format(fromMinor(minor, currencyCode));
}
